import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Check, LayoutGrid } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import { useAllCompanies, CompanyOption } from '../hooks/useAllCompanies';

interface SuperAdminCompanySwitcherProps {
  variant?: 'header' | 'drawer';
}

// Lets SUPERADMIN switch between "All Companies" (merged view across every company,
// the default) and one specific company (existing impersonation behaviour, unchanged).
// Independent of ResellerCompanySwitcher: different user type, different company
// source (every company in the system, not just ones a reseller created), and a third
// state ("all", merged) that switcher has no equivalent of.
const SuperAdminCompanySwitcher: React.FC<SuperAdminCompanySwitcherProps> = ({ variant = 'header' }) => {
  const { user } = useAuth();
  const { scope, setScope } = useSuperAdminCompany();
  const { data } = useAllCompanies();
  const companies: CompanyOption[] = data || [];
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (user?.role !== 'SUPERADMIN') return null;

  const choose = (company: CompanyOption | null) => {
    setOpen(false);
    const alreadyActive = company
      ? scope.mode === 'single' && scope.company.tenantId === company.tenantId
      : scope.mode === 'all';
    if (alreadyActive) return;
    setScope(company ? { mode: 'single', company } : { mode: 'all' });
    // Every domain's data was cached for the previous scope — refetch through the new one.
    queryClient.invalidateQueries();
  };

  const activeLabel = scope.mode === 'all' ? 'All Companies' : scope.company.name;

  if (variant === 'drawer') {
    return (
      <div className="px-3 pb-3">
        <p className="px-3 pb-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Viewing</p>
        <div className="space-y-0.5">
          <button
            onClick={() => choose(null)}
            className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
              scope.mode === 'all' ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> All Companies</span>
            {scope.mode === 'all' && <Check className="h-4 w-4 flex-shrink-0" />}
          </button>
          {companies.map((c) => {
            const active = scope.mode === 'single' && scope.company.tenantId === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c)}
                className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
                  active ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2"><Building2 className="h-4 w-4 flex-shrink-0" /> {c.name}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="View company"
        className="flex items-center gap-2 h-11 px-3 rounded-xl border bg-[#F2F2F7] border-[#E8EBF1] text-[#1B202A] hover:bg-[#EAEBF0] transition-colors"
      >
        <LayoutGrid className="h-5 w-5 flex-shrink-0 text-[#0055a9]" />
        <span className="text-sm font-semibold truncate max-w-[9rem] hidden sm:block">{activeLabel}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-[80px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[240px] bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] border border-[#E1E6EF] py-1 z-[1002] max-h-[70vh] overflow-y-auto">
          <button
            onClick={() => choose(null)}
            className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
              scope.mode === 'all' ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
            }`}
          >
            <span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> All Companies</span>
            {scope.mode === 'all' && <Check className="h-4 w-4 flex-shrink-0" />}
          </button>
          <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Companies
          </div>
          {companies.map((c) => {
            const active = scope.mode === 'single' && scope.company.tenantId === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                  active ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
                }`}
              >
                <span className="truncate">{c.name}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SuperAdminCompanySwitcher;
